import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import './SponsorCarousel.css';

/**
 * Homepage sponsor carousel.
 *
 * A continuous, seamless drift of sponsor cards. Every few seconds one fully
 * visible card, picked at random, gets a short "spotlight": a soft light sweep
 * across the logo panel, a slight lift and a gold hairline. The animation runs
 * on requestAnimationFrame with an eased velocity, so hovering or focusing a
 * card glides the row to a stop instead of snapping.
 *
 * The motion pauses when the row is off screen or the tab is hidden. With
 * "reduce motion" turned on it becomes a plain, swipeable row with no drift
 * and no spotlight.
 */
interface SponsorCarouselProps<T> {
  items: T[];
  getKey: (item: T) => string;
  /** `isClone` copies exist only to make the loop seamless; keep them out of the tab order. */
  renderItem: (item: T, isClone: boolean) => React.ReactNode;
  /** Drift speed in px per second. */
  speed?: number;
  label?: string;
}

const SPOTLIGHT_MS = 1700;

function SponsorCarousel<T>({ items, getKey, renderItem, speed = 34, label = 'Sponsors' }: SponsorCarouselProps<T>) {
  const viewportRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLUListElement>(null);
  const [copies, setCopies] = useState(2);
  const [reduceMotion, setReduceMotion] = useState(
    () => typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
  );

  // Motion state lives in refs so the 60fps loop never re-renders React.
  const setWidth = useRef(0);
  const offset = useRef(0);
  const velocity = useRef(speed);
  const held = useRef(false); // hover / focus / touch
  const visible = useRef(true);

  useEffect(() => {
    const mq = window.matchMedia?.('(prefers-reduced-motion: reduce)');
    if (!mq) return;
    const onChange = () => setReduceMotion(mq.matches);
    mq.addEventListener?.('change', onChange);
    return () => mq.removeEventListener?.('change', onChange);
  }, []);

  // Measure one set (including the gap after it) and render enough copies to
  // cover the viewport plus one set of runway for the wrap.
  useLayoutEffect(() => {
    if (reduceMotion) return;
    const viewport = viewportRef.current;
    const track = trackRef.current;
    if (!viewport || !track) return;
    const measure = () => {
      const n = items.length;
      const first = track.children[0] as HTMLElement | undefined;
      const nextSet = track.children[n] as HTMLElement | undefined;
      if (!first || !nextSet) return;
      const w = nextSet.offsetLeft - first.offsetLeft;
      if (w <= 0) return;
      setWidth.current = w;
      const needed = Math.max(2, Math.ceil(viewport.clientWidth / w) + 1);
      setCopies((c) => (c === needed ? c : needed));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(viewport);
    ro.observe(track);
    return () => ro.disconnect();
  }, [items.length, reduceMotion, copies]);

  // Drift loop.
  useEffect(() => {
    if (reduceMotion) return;
    const track = trackRef.current;
    if (!track) return;
    let raf = 0;
    let last = performance.now();
    const tick = (now: number) => {
      const dt = Math.min(0.05, (now - last) / 1000);
      last = now;
      const target = held.current || !visible.current ? 0 : speed;
      // Ease toward the target speed (~0.35s time constant).
      velocity.current += (target - velocity.current) * Math.min(1, dt * 3);
      const w = setWidth.current;
      if (w > 0) {
        offset.current = (offset.current + velocity.current * dt) % w;
        track.style.transform = `translate3d(${-offset.current}px,0,0)`;
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [reduceMotion, speed]);

  // Pause off screen and in background tabs.
  useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport) return;
    const io = new IntersectionObserver(([entry]) => {
      visible.current = entry.isIntersecting && !document.hidden;
    });
    io.observe(viewport);
    const onVis = () => {
      if (document.hidden) visible.current = false;
      else {
        const r = viewport.getBoundingClientRect();
        visible.current = r.bottom > 0 && r.top < window.innerHeight;
      }
    };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      io.disconnect();
      document.removeEventListener('visibilitychange', onVis);
    };
  }, []);

  // Random spotlight on a fully visible card.
  useEffect(() => {
    if (reduceMotion || items.length === 0) return;
    let timer = 0;
    let clear = 0;
    let lastIndex = -1;
    const schedule = () => {
      timer = window.setTimeout(fire, 2600 + Math.random() * 2200);
    };
    const fire = () => {
      const viewport = viewportRef.current;
      const track = trackRef.current;
      if (viewport && track && visible.current && !held.current) {
        const vr = viewport.getBoundingClientRect();
        const inset = vr.width * 0.1; // skip cards under the edge fade
        const cards = Array.from(track.querySelectorAll<HTMLElement>('.spc-card'));
        const candidates = cards.filter((el) => {
          const r = el.getBoundingClientRect();
          return r.left >= vr.left + inset && r.right <= vr.right - inset;
        });
        // Avoid lighting the same sponsor twice in a row.
        const fresh = candidates.filter((el) => Number(el.dataset.index) !== lastIndex);
        const pool = fresh.length ? fresh : candidates;
        const pick = pool[Math.floor(Math.random() * pool.length)];
        if (pick) {
          lastIndex = Number(pick.dataset.index);
          pick.classList.remove('is-spotlit');
          void pick.offsetWidth; // restart the animation if it was mid-run
          pick.classList.add('is-spotlit');
          clear = window.setTimeout(() => pick.classList.remove('is-spotlit'), SPOTLIGHT_MS);
        }
      }
      schedule();
    };
    schedule();
    return () => {
      window.clearTimeout(timer);
      window.clearTimeout(clear);
      trackRef.current?.querySelectorAll('.is-spotlit').forEach((el) => el.classList.remove('is-spotlit'));
    };
  }, [reduceMotion, items.length]);

  const hold = () => { held.current = true; };
  const release = () => { held.current = false; };

  const sets = reduceMotion ? 1 : copies;
  return (
    <div
      ref={viewportRef}
      className={`spc ${reduceMotion ? 'spc--static' : ''}`}
      role="region"
      aria-label={label}
      onMouseEnter={hold}
      onMouseLeave={release}
      onFocus={hold}
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) release();
      }}
      onTouchStart={hold}
      onTouchEnd={() => window.setTimeout(release, 1200)}
    >
      <ul ref={trackRef} className="spc-track">
        {Array.from({ length: sets }).flatMap((_, copy) =>
          items.map((item, i) => (
            <li
              key={`${copy}-${getKey(item)}`}
              className="spc-card"
              data-index={i}
              aria-hidden={copy > 0 ? true : undefined}
            >
              {renderItem(item, copy > 0)}
            </li>
          ))
        )}
      </ul>
    </div>
  );
}

export default SponsorCarousel;
